import { hash as hashIt } from 'hash-it';

import { LOOP_WINDOW_MS } from '../constants/constants';

const timestampsByHash = new Map<string, number[]>();

/** Max unique hashes tracked at once to bound memory in long sessions. */
const MAX_TRACKED_HASHES = 500;

const getActionHash = (action: unknown) => {
  const hashable = { ...(action as Record<string, unknown>) };
  delete hashable.analytics;
  delete hashable.origin;
  return String(hashIt(hashable));
};

/**
 * Returns true when the same action has been invoked more than `maxCount` times
 * within the sliding window. The hash is cleared immediately on detection so
 * the next invocation starts fresh.
 */
export const checkLoop = (
  action: unknown,
  maxCount: number,
  windowMs = LOOP_WINDOW_MS,
): boolean => {
  const actionHash = getActionHash(action);
  const now = Date.now();
  const cutoff = now - windowMs;

  // Only clean stale timestamps for the current key, not the entire map
  const recentTimestamps = (timestampsByHash.get(actionHash) ?? []).filter(
    (timestamp) => timestamp > cutoff,
  );

  recentTimestamps.push(now);

  if (recentTimestamps.length > maxCount) {
    timestampsByHash.delete(actionHash);
    return true;
  }

  // Enforce max-size cap: evict oldest entry when limit is reached
  if (
    !timestampsByHash.has(actionHash) &&
    timestampsByHash.size >= MAX_TRACKED_HASHES
  ) {
    const oldestKey = timestampsByHash.keys().next().value as string;
    timestampsByHash.delete(oldestKey);
  }

  timestampsByHash.set(actionHash, recentTimestamps);
  return false;
};
